import { Snapshot } from "@/shared/types";
import { useEffect } from "react";
import { Euler, Vector3 } from "three";

export const getDefaultCameraProps = (): {
  position: [number, number, number];
  rotation: Euler;
  zoom?: undefined;
} => {
  const storedCamera = localStorage.getItem("camera");

  if (storedCamera) {
    const parsedCamera = JSON.parse(storedCamera);

    return {
      position: parsedCamera.position,
      rotation: parsedCamera.rotation,
      zoom: parsedCamera.zoom,
    };
  }

  return {
    position: [1.699468015743732, 2.1081588090894594, -4.420112764647251],
    rotation: new Euler(
      -1.6901262320811703,
      -1.259540496018465,
      -1.6960881347247334,
      "XYZ"
    ),
  };
};

export const saveCameraProps = ({
  position,
  rotation,
  zoom,
  target,
}: {
  position: number[];
  rotation: (string | number | undefined)[];
  zoom: string;
  target: number[];
}) => {
  localStorage.setItem(
    "camera",
    JSON.stringify({
      position,
      rotation,
      zoom,
      target,
    })
  );
};

const DEFAULT_TARGET = [
  13.190994719426003, 3.7974126588102814e-17, -2.9292969563836637,
];

export const useDefaultTarget = (set: (value: number[]) => void) => {
  useEffect(() => {
    const storedData = localStorage.getItem("camera");

    if (storedData) {
      const { target } = JSON.parse(storedData);

      set(target);
    } else {
      set(DEFAULT_TARGET);
    }
  }, []);
};

export const midpoint = (
  [x1, y1, z1]: number[],
  [x2, y2, z2]: number[]
): [number, number, number] => [(x1 + x2) / 2, (y1 + y2) / 2, (z1 + z2) / 2];

export const swapElements = (
  array: number[],
  index1: number,
  index2: number
) => {
  array[index1] = array.splice(index2, 1, array[index1])[0];
};

const isZeroPoint = (point: [number, number, number]) =>
  point[0] === 0 && point[1] === 0 && point[2] === 0;

export const getShoulderMidpoint = (
  snapshot: Snapshot | null
): [number, number, number] => {
  if (!snapshot) return [0, 0, 0];

  if (
    isZeroPoint([snapshot.pose_x[5], snapshot.pose_y[5], snapshot.pose_z[5]]) &&
    !isZeroPoint([snapshot.pose_x[6], snapshot.pose_y[6], snapshot.pose_z[6]])
  )
    return [snapshot.pose_x[6], snapshot.pose_z[6] + 0.5, -snapshot.pose_y[6]];

  if (
    !isZeroPoint([
      snapshot.pose_x[5],
      snapshot.pose_y[5],
      snapshot.pose_z[5],
    ]) &&
    isZeroPoint([snapshot.pose_x[6], snapshot.pose_y[6], snapshot.pose_z[6]])
  )
    return [snapshot.pose_x[5], snapshot.pose_z[5] + 0.5, -snapshot.pose_y[5]];

  return midpoint(
    [snapshot.pose_x[6], snapshot.pose_z[6] + 0.5, -snapshot.pose_y[6]],
    [snapshot.pose_x[5], snapshot.pose_z[5] + 0.5, -snapshot.pose_y[5]]
  );
};

export function extendLineSegment(
  A: Vector3,
  B: Vector3,
  distance = 1
): Vector3 {
  // Calculate the direction vector from A to B
  const Dx = B.x - A.x;
  const Dy = B.y - A.y;
  const Dz = B.z - A.z;

  // Calculate the magnitude of the direction vector
  const magnitude = Math.sqrt(Dx * Dx + Dy * Dy + Dz * Dz);

  // Normalize the direction vector
  const DxNormalized = Dx / magnitude;
  const DyNormalized = Dy / magnitude;
  const DzNormalized = Dz / magnitude;

  // Extend the line segment by a length of 1
  const Cx = B.x + DxNormalized * distance;
  const Cy = B.y + DyNormalized * distance;
  const Cz = B.z + DzNormalized * distance;

  // Return the coordinates of the extended endpoint C
  return new Vector3(Cx, Cy, Cz);
}
